// SPDX-FileCopyrightText: 2025 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import isEqual from "lodash/isEqual";
import { getTranslationLabel, isSecretAttribute, userBasicInfo } from "@/services/UserService";
export var formatFieldValue = function (value, t) {
    if (value === null || value === undefined || value === "") {
        return "-";
    }
    if (typeof value === "boolean") {
        return value ? t("common.label.yes") : t("common.label.no");
    }
    if (Array.isArray(value)) {
        return value.length > 0 ? value.join(", ") : "-";
    }
    return String(value);
};
var formatAreaValue = function (area, t) {
    var _a;
    return formatFieldValue((_a = area === null || area === void 0 ? void 0 : area.name) !== null && _a !== void 0 ? _a : area === null || area === void 0 ? void 0 : area.id, t);
};
var valuesEqual = function (a, b) {
    if (Array.isArray(a) || Array.isArray(b)) {
        var arrayA = Array.isArray(a) ? __spreadArray([], a, true).sort() : [];
        var arrayB = Array.isArray(b) ? __spreadArray([], b, true).sort() : [];
        return isEqual(arrayA, arrayB);
    }
    return (a !== null && a !== void 0 ? a : "") === (b !== null && b !== void 0 ? b : "");
};
/**
 * Diffs the in-progress edit-voter draft against the baseline captured when
 * the edit drawer loaded. Mirrors the field set/sourcing that
 * EditUserForm's renderFormField actually displays (userBasicInfo fields
 * live on `user` directly, everything else lives in `user.attributes`),
 * so the review table never reports a field that isn't actually editable.
 */
export var computeUserDiff = function (baseline, current, userAttributes, t) {
    var _a, _b, _c, _d, _e, _f;
    var rows = [];
    var areaAttribute = userAttributes.find(function (attr) { var _a; return (_a = attr.name) === null || _a === void 0 ? void 0 : _a.toLowerCase().includes("area"); });
    var pushIfChanged = function (field, label, oldValue, newValue) {
        if (!valuesEqual(oldValue, newValue)) {
            rows.push({
                field: field,
                label: label,
                currentValue: formatFieldValue(oldValue, t),
                newValue: formatFieldValue(newValue, t),
            });
        }
    };
    // "enabled" is rendered as a standalone checkbox outside the userAttributes loop.
    pushIfChanged("enabled", t("usersAndRolesScreen.users.fields.enabled"), baseline.user.enabled, (_a = current.user) === null || _a === void 0 ? void 0 : _a.enabled);
    if (!valuesEqual((_b = baseline.user.area) === null || _b === void 0 ? void 0 : _b.id, (_d = (_c = current.user) === null || _c === void 0 ? void 0 : _c.area) === null || _d === void 0 ? void 0 : _d.id)) {
        rows.push({
            field: (_e = areaAttribute === null || areaAttribute === void 0 ? void 0 : areaAttribute.name) !== null && _e !== void 0 ? _e : "area",
            label: areaAttribute
                ? getTranslationLabel(areaAttribute.name, areaAttribute.display_name, t)
                : t("usersAndRolesScreen.users.fields.area"),
            currentValue: formatAreaValue(baseline.user.area, t),
            newValue: formatAreaValue((_f = current.user) === null || _f === void 0 ? void 0 : _f.area, t),
        });
    }
    userAttributes.forEach(function (attr) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q;
        var name = attr.name;
        if (!name) {
            return;
        }
        var lowerName = name.toLowerCase();
        var label = getTranslationLabel(name, attr.display_name, t);
        if (isSecretAttribute(attr)) {
            if (current.secretAttributeChanges &&
                Object.prototype.hasOwnProperty.call(current.secretAttributeChanges, name)) {
                rows.push({
                    field: name,
                    label: label,
                    currentValue: ((_b = (_a = baseline.user.attributes) === null || _a === void 0 ? void 0 : _a[name]) === null || _b === void 0 ? void 0 : _b.length) ? "••••••••" : "-",
                    newValue: current.secretAttributeChanges[name].length ? "••••••••" : "-",
                });
            }
            return;
        }
        // These substring checks intentionally mirror renderFormField's own attr.name
        // matching (EditUserForm.tsx), so a field is categorized here exactly the way
        // the edit form treats it. Area is rendered via a dedicated selector outside
        // the loop, so it's diffed once above against `user.area.id`.
        if (lowerName.includes("area")) {
            return;
        }
        // SelectActedTrustee auto-populates selectedActedTrustee from the baseline trustee
        // on mount; falling back to the baseline value here neutralizes that so it never
        // reports a spurious change until the admin actually picks a different trustee.
        if (lowerName.includes("trustee")) {
            var baselineTrustee = (_d = (_c = baseline.user.attributes) === null || _c === void 0 ? void 0 : _c[name]) === null || _d === void 0 ? void 0 : _d[0];
            var currentTrustee = current.selectedActedTrustee || baselineTrustee;
            pushIfChanged(name, label, baselineTrustee, currentTrustee);
            return;
        }
        if (lowerName.includes("mobile-number")) {
            var baselineValue = (_f = (_e = baseline.user.attributes) === null || _e === void 0 ? void 0 : _e[name]) === null || _f === void 0 ? void 0 : _f[0];
            var currentValue = (_h = (_g = current.phoneInputs[name]) === null || _g === void 0 ? void 0 : _g[0]) !== null && _h !== void 0 ? _h : (_l = (_k = (_j = current.user) === null || _j === void 0 ? void 0 : _j.attributes) === null || _k === void 0 ? void 0 : _k[name]) === null || _l === void 0 ? void 0 : _l[0];
            pushIfChanged(name, label, baselineValue, currentValue);
            return;
        }
        var isCustomAttribute = !userBasicInfo.includes(name);
        if (isCustomAttribute) {
            pushIfChanged(name, label, (_m = baseline.user.attributes) === null || _m === void 0 ? void 0 : _m[name], (_p = (_o = current.user) === null || _o === void 0 ? void 0 : _o.attributes) === null || _p === void 0 ? void 0 : _p[name]);
        }
        else if (name !== "username") {
            // username is always disabled in edit mode, so it's never diffed.
            pushIfChanged(name, label, baseline.user[name], (_q = current.user) === null || _q === void 0 ? void 0 : _q[name]);
        }
    });
    return rows;
};
export var computeRoleDiff = function (baseline, current, roles, t) {
    var baselineIds = new Set(baseline.activeRoleIds);
    var currentIds = new Set(current.activeRoleIds);
    return roles
        .filter(function (role) { return role.id; })
        .filter(function (role) { return baselineIds.has(role.id) !== currentIds.has(role.id); })
        .sort(function (left, right) { var _a, _b, _c, _d; return ((_b = (_a = left.name) !== null && _a !== void 0 ? _a : left.id) !== null && _b !== void 0 ? _b : "").localeCompare((_d = (_c = right.name) !== null && _c !== void 0 ? _c : right.id) !== null && _d !== void 0 ? _d : ""); })
        .map(function (role) {
        var _a;
        var roleId = role.id;
        return {
            field: "role:".concat(roleId),
            label: (_a = role.name) !== null && _a !== void 0 ? _a : roleId,
            currentValue: baselineIds.has(roleId)
                ? t("common.label.yes")
                : t("common.label.no"),
            newValue: currentIds.has(roleId) ? t("common.label.yes") : t("common.label.no"),
        };
    });
};
